import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '../../../layout/Icon.jsx';
import { useLogin } from '../hooks/useLogin.js';
import { rememberedCompany } from '../../../shared/useAuth.js';
import { toast } from '../../../shared/useToast.js';

const schema = Joi.object({
  // كود الشركة: مطلوب فقط على خادم يخدم أكثر من شركة.
  company: Joi.string().trim().lowercase().allow('').optional(),
  username: Joi.string().trim().min(2).required().messages({
    'string.empty': 'اسم المستخدم مطلوب',
    'string.min': 'اسم المستخدم قصير جداً',
    'any.required': 'اسم المستخدم مطلوب'
  }),
  password: Joi.string().min(6).required().messages({
    'string.empty': 'كلمة المرور مطلوبة',
    'string.min': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
    'any.required': 'كلمة المرور مطلوبة'
  }),
  remember: Joi.boolean().optional()
});

export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { submit, loading, error, reset } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);
  const saved = rememberedCompany();
  // الحقل مخفي على التنصيب ذي الشركة الواحدة، ويظهر إن سبق استعماله أو طلبه الخادم.
  const [showCompany, setShowCompany] = useState(!!saved);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: { company: saved, username: '', password: '', remember: true }
  });

  const onSubmit = async (values) => {
    reset();
    const result = await submit({
      username: values.username.trim(),
      password: values.password,
      company: (values.company || '').trim()
    });
    if (result.code === 'COMPANY_REQUIRED') setShowCompany(true);
    if (result.ok) {
      toast.success('مرحباً بك في Kids Zone');
      const redirect = location.state?.from?.pathname || '/dashboard';
      navigate(redirect, { replace: true });
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }
  };

  const onForgot = (e) => {
    e.preventDefault();
    toast.info('تواصل مع المدير لإعادة تعيين كلمة المرور');
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={clsx('w-full max-w-[400px]', shake && 'animate-pulse')}
      noValidate
    >
      <h2 className="text-[26px] font-extrabold m-0 tracking-tight">تسجيل الدخول</h2>
      <p className="text-ink-3 text-[13px] mt-1.5">أدخل بياناتك للوصول إلى لوحة الإدارة</p>

      {showCompany ? (
        <div className="mt-5">
          <label className="block text-[12px] font-semibold text-ink-2 mb-1.5">كود الشركة</label>
          <div className="relative">
            <span className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3">
              <Icon name="dashboard" size={16} />
            </span>
            <input
              type="text"
              dir="ltr"
              autoComplete="organization"
              className={clsx('input pr-9 text-left', errors.company && 'border-red')}
              placeholder="company-code"
              {...register('company')}
            />
          </div>
          <div className="text-ink-3 text-[11px] mt-1">الكود الذي زوّدتك به إدارة النظام</div>
        </div>
      ) : null}

      <div className="mt-5">
        <label className="block text-[12px] font-semibold text-ink-2 mb-1.5">اسم المستخدم</label>
        <div className="relative">
          <span className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3">
            <Icon name="users" size={16} />
          </span>
          <input
            type="text"
            autoComplete="username"
            autoFocus
            className={clsx('input pr-9', errors.username && 'border-red')}
            placeholder="user.name"
            {...register('username')}
          />
        </div>
        {errors.username ? (
          <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.username.message}</div>
        ) : null}
      </div>

      <div className="mt-3">
        <label className="block text-[12px] font-semibold text-ink-2 mb-1.5">كلمة المرور</label>
        <div className="relative">
          <span className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3">
            <Icon name="settings" size={16} />
          </span>
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            className={clsx('input pr-9 pl-10', errors.password && 'border-red')}
            placeholder="••••••••"
            {...register('password')}
          />
          <button
            type="button"
            className="absolute top-1/2 -translate-y-1/2 left-2 text-ink-3 hover:text-ink p-1"
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
            title={showPassword ? 'إخفاء' : 'عرض'}
          >
            {showPassword ? <Icon name="x" size={14} /> : <Icon name="check" size={14} />}
          </button>
        </div>
        {errors.password ? (
          <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.password.message}</div>
        ) : null}
      </div>

      <div className="flex items-center justify-between mt-3 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer text-ink-2">
          <input type="checkbox" className="accent-orange" {...register('remember')} />
          تذكرني
        </label>
        <a
          href="#"
          onClick={onForgot}
          className="text-orange-deep font-semibold no-underline hover:underline"
        >
          نسيت كلمة المرور؟
        </a>
      </div>

      {error ? (
        <div className="mt-3 px-3 py-2.5 rounded-[10px] bg-[#FEE2E2] border border-[#FCA5A5] text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-2">
          <Icon name="x" size={14} />
          {error}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={loading}
        className={clsx(
          'btn btn-primary btn-xl w-full justify-center mt-4',
          loading && 'opacity-70 cursor-not-allowed'
        )}
      >
        {loading ? 'جاري الدخول...' : (
          <>
            تسجيل الدخول
            <Icon name="chevronL" size={16} />
          </>
        )}
      </button>

      {!showCompany ? (
        <div className="text-center mt-4">
          <button
            type="button"
            className="text-ink-3 text-[11.5px] font-semibold hover:text-ink-2 bg-transparent border-0 cursor-pointer"
            onClick={() => setShowCompany(true)}
          >
            الدخول بكود شركة
          </button>
        </div>
      ) : null}

      <div className="text-center mt-6 text-[11px] text-ink-3">محمي بتشفير AES-256 • سجل التدقيق مفعّل</div>
    </form>
  );
}
